import VerifiedIcon from '@mui/icons-material/Verified';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  TextField,
  Typography
} from '@mui/material';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useMediaQuery } from 'react-responsive';
import { formatCnpjCpf, formatCurrency } from '../../utils/formatters';

/**
 * @component DialogAuthorizeSale
 * @description Modal para autorização de venda com validação de comissão do vendedor
 */
export default function DialogAuthorizeSale({
  open = false,
  handleClose,
  handleAuthorize,
  saleData = null,
  loading = false
}) {
  const isMobile = useMediaQuery({ query: '(max-width: 1024px)' });
  const dialogRef = useRef(null);

  const [commissionValue, setCommissionValue] = useState('0');
  const [isValid, setIsValid] = useState(false);

  const commissionOptions = useMemo(() => ['0', '1', '2'], []);

  const commissionMenuProps = useMemo(() => ({
    disablePortal: true,
    disableScrollLock: true,
    container: dialogRef.current,
    PaperProps: {
      sx: {
        zIndex: (theme) => theme.zIndex.modal + 5
      }
    }
  }), []);

  // Resetar estado quando o modal abre/fecha
  useEffect(() => {
    if (open && saleData) {
      const commissionAmount = parseFloat(
        saleData.seller?.comissao ||
        saleData.seller?.commission ||
        saleData.user?.comissao ||
        saleData.user?.commission ||
        0
      );
      const normalized = commissionOptions.includes(String(commissionAmount))
        ? String(commissionAmount)
        : '0';
      setCommissionValue(normalized);
      setIsValid(true);
    } else {
      setCommissionValue('0');
      setIsValid(false);
    }
  }, [open, saleData, commissionOptions]);

  const handleCommissionChange = (event) => {
    const value = event.target.value;
    setCommissionValue(value);
    setIsValid(commissionOptions.includes(value));
  };

  const handleConfirmAuthorize = () => {
    if (isValid && handleAuthorize) {
      const numValue = parseFloat(commissionValue) || 0;
      handleAuthorize({
        ...saleData,
        commissionValue: numValue
      });
    }
  };

  const handleCancel = () => {
    if (!loading) {
      handleClose();
    }
  };

  if (!saleData) return null;

  return (
    <Dialog
      ref={dialogRef}
      open={open}
      onClose={!loading ? handleCancel : undefined}
      fullScreen={isMobile}
      maxWidth="sm"
      fullWidth
      sx={{
        zIndex: 9999,
        '& .MuiDialog-paper': isMobile ? {
          margin: 0,
          maxHeight: '100vh',
          height: '100vh',
          maxWidth: '100vw',
          width: '100vw',
          position: 'fixed',
          top: 0,
          left: 0
        } : {
          maxWidth: '600px',
          width: '85%',
          position: 'fixed'
        },
      }}
      disableEscapeKeyDown={!isMobile}
      keepMounted={false}
    >
      <DialogTitle sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        pb: 2,
        fontSize: isMobile ? 20 : 22,
        fontWeight: 'medium'
      }}>
        <VerifiedIcon sx={{ fontSize: isMobile ? 24 : 26, color: 'primary.main' }} />
        Autorizar venda
      </DialogTitle>

      <DialogContent sx={{ pt: 2, pb: 3 }}>
        <Box>
          {/* Linha 1: Campanha e Códigos */}
          <Grid container spacing={2} mb={2.5}>
            <Grid item xs={12} sm={4}>
              <Typography variant="caption" sx={{ fontSize: 14, display: 'block', marginBottom: 0.5, color: '#888', letterSpacing: 0.5, fontWeight: 500 }}>
                Campanha
              </Typography>
              <Typography sx={{ fontSize: 14, fontWeight: 600, color: '#1a1a1a' }}>
                {saleData.campaignName || 'N/A'}
              </Typography>
            </Grid>
            <Grid item xs={6} sm={4}>
              <Typography variant="caption" sx={{ fontSize: 14, display: 'block', marginBottom: 0.5, color: '#888', letterSpacing: 0.5, fontWeight: 500 }}>
                Código P.I.
              </Typography>
              <Typography sx={{ fontSize: 14, fontWeight: 600, color: '#1a1a1a' }}>
                {saleData.codPi || 'N/A'}
              </Typography>
            </Grid>
            <Grid item xs={6} sm={4}>
              <Typography variant="caption" sx={{ fontSize: 14, display: 'block', marginBottom: 0.5, color: '#888', letterSpacing: 0.5, fontWeight: 500 }}>
                Código Interno
              </Typography>
              <Typography sx={{ fontSize: 14, fontWeight: 600, color: '#1a1a1a' }}>
                {saleData.codIntern || 'N/A'}
              </Typography>
            </Grid>
          </Grid>

          {/* Linha 2: Cliente */}
          <Grid container spacing={2} mb={2.5}>
            <Grid item xs={12} sm={8}>
              <Typography variant="caption" sx={{ fontSize: 14, display: 'block', marginBottom: 0.5, color: '#888', letterSpacing: 0.5, fontWeight: 500 }}>
                Cliente
              </Typography>
              <Typography sx={{ fontSize: 14, fontWeight: 600, color: '#1a1a1a' }}>
                {saleData.customerName || 'N/A'}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Typography variant="caption" sx={{ fontSize: 14, display: 'block', marginBottom: 0.5, color: '#888', letterSpacing: 0.5, fontWeight: 500 }}>
                CPF/CNPJ
              </Typography>
              <Typography sx={{ fontSize: 14, fontWeight: 600, color: '#1a1a1a' }}>
                {saleData.customerDocument ? formatCnpjCpf(saleData.customerDocument) : 'N/A'}
              </Typography>
            </Grid>
          </Grid>

          {/* Linha 3: Métricas */}
          <Grid container spacing={2} mb={2.5}>
            <Grid item xs={4}>
              <Typography variant="caption" sx={{ fontSize: 11, display: 'block', marginBottom: 0.5, color: '#888', letterSpacing: 0.5, fontWeight: 500 }}>
                Qtd Painéis
              </Typography>
              <Typography sx={{ fontSize: 16, fontWeight: 700, color: '#1a1a1a' }}>
                {saleData.qtdPanels || 0}
              </Typography>
            </Grid>
            <Grid item xs={4}>
              <Typography variant="caption" sx={{ fontSize: 11, display: 'block', marginBottom: 0.5, color: '#888', letterSpacing: 0.5, fontWeight: 500 }}>
                Ticket Médio
              </Typography>
              <Typography sx={{ fontSize: 16, fontWeight: 700, color: '#1a1a1a' }}>
                {formatCurrency(saleData.ticketMiddle || 0)}
              </Typography>
            </Grid>
            <Grid item xs={4}>
              <Typography variant="caption" sx={{ fontSize: 11, display: 'block', marginBottom: 0.5, color: '#888', letterSpacing: 0.5, fontWeight: 500 }}>
                Qtd Dias
              </Typography>
              <Typography sx={{ fontSize: 16, fontWeight: 700, color: '#1a1a1a' }}>
                {saleData.qtdDays || 0}
              </Typography>
            </Grid>
          </Grid>

          {/* Valor Total */}
          <Box mb={3} sx={{ backgroundColor: '#f8f9fa', padding: 2, borderRadius: 1 }}>
            <Typography variant="caption" sx={{ fontSize: 11, display: 'block', marginBottom: 0.5, color: '#888', letterSpacing: 0.5, fontWeight: 500 }}>
              Valor Total a Faturar
            </Typography>
            <Typography sx={{ fontSize: 24, fontWeight: 700, color: '#1976d2' }}>
              {formatCurrency(saleData.vlTotal || 0)}
            </Typography>
          </Box>

          {/* Vendedor e Comissão */}
          <Grid container spacing={2} mb={2.5}>
            <Grid item xs={12} sm={8}>
              <Typography variant="caption" sx={{ fontSize: 16, display: 'block', marginBottom: 0.5, color: '#888', letterSpacing: 0.5, fontWeight: 500 }}>
                Vendedor
              </Typography>
              <Typography sx={{ fontSize: 14, fontWeight: 600, color: '#1a1a1a' }}>
                {saleData.sellerName || 'N/A'}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Typography variant="caption" sx={{ fontSize: 11, display: 'block', marginBottom: 1, color: '#888', letterSpacing: 0.5, fontWeight: 500 }}>
                Percentual da Comissão
              </Typography>
              <TextField
                select
                fullWidth
                value={commissionValue}
                onChange={handleCommissionChange}
                disabled={loading}
                size="small"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    fontSize: 14,
                    fontWeight: 600
                  }
                }}
                SelectProps={{
                  MenuProps: commissionMenuProps
                }}
              >
                {commissionOptions.map((option) => (
                  <MenuItem key={option} value={option}>
                    {`${option}%`}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>

        </Box>
      </DialogContent>

      <DialogActions>
        <Button
          onClick={handleCancel}
          disabled={loading}
          color="warning"
          variant="contained"
        >
          Cancelar
        </Button>
        <Button
          onClick={handleConfirmAuthorize}
          disabled={!isValid || loading}
          variant="contained"
          color="primary"
        >
          {loading ? 'Autorizando...' : 'Autorizar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
